// Helper simple para manejar sesión con expiración en localStorage.
//
// Scope-aware: Pre-SISI (src/presisi/) tiene su propio login, separado del login
// principal, para que entrar a uno no cierre la sesión del otro — aunque ambos
// autentican contra el mismo backend /auth/login (las mismas cuentas). Cada scope
// guarda su token bajo llaves distintas de localStorage. El scope "activo" lo fija el
// módulo que está montado (ver setActiveAuthScope en PreSisiApp.tsx) para que el
// interceptor de axios en api.ts sepa cuál leer sin que cada llamada deba indicarlo.
export type AuthScope = 'main' | 'presisi';

let activeScope: AuthScope = 'main';
export const setActiveAuthScope = (scope: AuthScope) => { activeScope = scope; };
export const getActiveAuthScope = (): AuthScope => activeScope;

// Ruta de login a la que redirigir según el scope activo (usada por api.ts cuando una
// petición protegida no tiene token válido).
export const getLoginPath = (scope: AuthScope = activeScope): string =>
  scope === 'presisi' ? '/pre-sisi/login' : '/login';

const KEYS: Record<AuthScope, { token: string; user: string; expires: string }> = {
  main: { token: 'token', user: 'user', expires: 'session_expires_at' },
  presisi: { token: 'presisi_token', user: 'presisi_user', expires: 'presisi_session_expires_at' },
};

export function setSession(token: string, user: any, durationMinutes = 30, scope: AuthScope = activeScope) {
  try {
    const k = KEYS[scope];
    const expiresAt = Date.now() + durationMinutes * 60 * 1000; // duration in minutes
    localStorage.setItem(k.token, token);
    localStorage.setItem(k.user, JSON.stringify(user));
    localStorage.setItem(k.expires, String(expiresAt));
  } catch (e) {
    // storage may not be available in some environments (SSR/tests)
  }
}

export function clearSession(scope: AuthScope = activeScope) {
  try {
    const k = KEYS[scope];
    localStorage.removeItem(k.token);
    localStorage.removeItem(k.user);
    localStorage.removeItem(k.expires);
  } catch (e) {}
}

export function getTokenIfValid(scope: AuthScope = activeScope): string | null {
  try {
    const k = KEYS[scope];
    const token = localStorage.getItem(k.token);
    const expStr = localStorage.getItem(k.expires);
    const exp = expStr ? parseInt(expStr, 10) : 0;
    if (!token) return null;
    if (Date.now() > exp) {
      clearSession(scope);
      return null;
    }
    return token;
  } catch (e) {
    return null;
  }
}

export function getExpiresAt(scope: AuthScope = activeScope): number | null {
  try {
    const k = KEYS[scope];
    const expStr = localStorage.getItem(k.expires);
    return expStr ? parseInt(expStr, 10) : null;
  } catch (e) {
    return null;
  }
}

// Devuelve ms restantes (>=0) o 0 si expirado/no disponible
export function remainingMs(scope: AuthScope = activeScope): number {
  const exp = getExpiresAt(scope);
  if (!exp) return 0;
  return Math.max(0, exp - Date.now());
}

// Programa un auto-logout llamando `onLogout` cuando expire la sesión.
// Devuelve una función para limpiar el timeout.
export function scheduleAutoLogout(onLogout: () => void, scope: AuthScope = activeScope) {
  try {
    const ms = remainingMs(scope);
    if (ms <= 0) {
      // Ejecutar inmediatamente si ya expiró
      onLogout();
      return () => {};
    }
    const id = window.setTimeout(onLogout, ms);
    return () => window.clearTimeout(id);
  } catch (e) {
    return () => {};
  }
}

const sessionHelpers = {
  setSession,
  clearSession,
  getTokenIfValid,
  getExpiresAt,
  remainingMs,
  scheduleAutoLogout,
  setActiveAuthScope,
  getActiveAuthScope,
  getLoginPath,
};

export default sessionHelpers;
